class BundleForm {
  constructor() {
    this.upsellContainer = document.querySelector('.goshop-mp-hu');
    if (!this.upsellContainer) {
      return;
    }
  }

  isBundleProduct() {
    return !!this.upsellContainer;
  }

  isMultiColorBundle() {
    if (!this.isBundleProduct()) return false;
    const requiredColors = parseInt(this.upsellContainer.dataset.requiredColors, 10) || 1;
    return requiredColors > 1;
  }

  formatPatternName(name) {
    return name.replace('Kit', 'Pattern').trim();
  }

  parseQuantityValue(quantityData) {
    try {
      const parsed = JSON.parse(quantityData);
      if (Array.isArray(parsed)) {
        return parseInt(parsed[0]) || 1;
      }
      return parseInt(parsed) || 1;
    } catch {
      return parseInt(quantityData) || 1;
    }
  }

  groupSlotsByColor(filledSlots) {
    const colorGroups = {};

    filledSlots.forEach((slot) => {
      const key = slot.variantId;

      if (colorGroups[key]) {
        colorGroups[key].totalQuantity += parseInt(slot.quantity) || 0;
      } else {
        colorGroups[key] = {
          variantId: slot.variantId,
          sku: slot.sku,
          title: slot.title,
          cleanTitle: slot.cleanTitle,
          totalQuantity: parseInt(slot.quantity) || 0,
        };
      }
    });

    return Object.values(colorGroups);
  }

  getNeedlesData() {
    const needlesContainer = document.querySelector('[data-needles-container]');

    if (!needlesContainer) {
      return null;
    }

    const needleQuantity = parseInt(needlesContainer.dataset.selectedNeedleQuantity, 10) || 0;

    if (needleQuantity === 0) {
      return null;
    }

    const needleName = needlesContainer.dataset.selectedNeedleName;
    const needleSku = needlesContainer.dataset.selectedNeedleSku;
    const needleId = needlesContainer.dataset.selectedNeedleId;

    if (!needleName || !needleSku || !needleId) {
      return null;
    }

    return {
      quantity: needleQuantity,
      name: needleName,
      sku: needleSku,
      id: needleId,
    };
  }

  async prepareFormData(formData) {
    if (!this.isBundleProduct()) {
      return formData;
    }

    // The page's `.goshop-mp-hu` container describes the page's own kit. A form
    // that already carries its kit properties (snippets/mystery-kit-fields.liquid,
    // e.g. a mystery kit's product card on another kit's page) must keep them
    // instead of getting this page's kit appended on top.
    if (formData.has('properties[_bundle]')) {
      return formData;
    }

    const packaging = this.upsellContainer.dataset.packaging;
    const weight = this.upsellContainer.dataset.weight;
    let colorVariants = [];
    let colorDescriptions = '';

    const isMultiColor = this.isMultiColorBundle();
    const noColorSelection = this.upsellContainer.dataset.noColorSelection === 'true';

    if (isMultiColor) {
      const selectedColorSlots = JSON.parse(this.upsellContainer.dataset.selectedColorSlots || '[]');
      const requiredColors = parseInt(this.upsellContainer.dataset.requiredColors) || 1;
      const filledSlots = selectedColorSlots.filter((slot) => slot !== null);

      if (filledSlots.length !== requiredColors) {
        const template =
          window?.variantStrings?.missingColorsOnBundle || 'Please select [required_colors] colors for this bundle.';
        throw new Error(template.replace(/\[required_colors\]/g, String(requiredColors)));
      }

      const isPack = this.upsellContainer.dataset.isPack === 'true';

      if (isPack) {
        const groupedColors = this.groupSlotsByColor(filledSlots);

        colorVariants = groupedColors.map((group) => ({
          id: group.variantId,
          sku: group.sku,
          title: group.title,
          quantity: group.totalQuantity,
        }));

        colorDescriptions = groupedColors.map((group) => `x${group.totalQuantity} ${group.cleanTitle}`).join(', ');
      } else {
        colorVariants = filledSlots.map((slot) => ({
          id: slot.variantId,
          sku: slot.sku,
          title: slot.title,
          quantity: slot.quantity,
        }));

        colorDescriptions = filledSlots.map((slot) => `x${slot.quantity} ${slot.cleanTitle}`).join(', ');
      }
    } else if (!noColorSelection) {
      const selectedColorSlots = JSON.parse(this.upsellContainer.dataset.selectedColorSlots || '[]');
      const firstSlot = selectedColorSlots[0];

      if (!firstSlot) {
        const template = window?.variantStrings?.missingColorsOnBundle || 'Please select a color for this bundle.';
        throw new Error(template.replace(/\[required_colors\]/g, '1'));
      }

      const colorVariant = {
        id: firstSlot.variantId,
        sku: firstSlot.sku,
        title: firstSlot.title,
        quantity: this.parseQuantityValue(firstSlot.quantity),
      };

      colorVariants = [colorVariant];
      const titleForDescription = firstSlot.cleanTitle || firstSlot.title || '';
      colorDescriptions = `x${colorVariant.quantity} ${titleForDescription}`;
    }

    let pattern = null;
    if (this.upsellContainer.dataset.patternUrl && this.upsellContainer.dataset.patternName) {
      pattern = {
        type: 'pattern',
        url: this.upsellContainer.dataset.patternUrl,
        title: this.formatPatternName(this.upsellContainer.dataset.patternName),
      };
    }

    const needles = this.getNeedlesData();

    let bundleArray = [...colorVariants];
    if (needles) {
      bundleArray.push(needles);
    }
    if (pattern) {
      bundleArray.push(pattern);
    }

    formData.append('properties[color]', colorDescriptions);
    formData.append('properties[_bundle]', JSON.stringify(bundleArray));
    formData.append('properties[_packaging]', packaging);
    formData.append('properties[_weight]', weight);

    // The _preorder flag and its visible companion property used to be
    // appended here, keyed on this.upsellContainer.dataset.hasPreorderColor.
    // They are now two hidden, disabled-by-default inputs rendered by
    // snippets/preorder-form-fields.liquid inside the form itself
    // (buy-buttons.liquid), toggled live by applyPreorderState() on every
    // preorder:change — the same event that sets hasPreorderColor in the
    // first place. FormData already picks them up naturally at submit time,
    // so there is nothing left to append here. Moved so the standalone/
    // "mystery" preorder path (no .goshop-mp-hu color pick at all) can reuse
    // the exact same two inputs instead of never writing them, which was the
    // original gap this comment used to paper over.

    return formData;
  }
}
